import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	Card,
	Row,
	Col,
	Statistic,
	Typography,
	Spin,
	Alert,
	Divider,
	message,
	Descriptions,
	Button,
	Modal,
	Input,
	Select,
	Tabs,
} from 'antd';
import styled from 'styled-components';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../methods/redux/hooks';
import {
	getRemittanceReport,
	rescanSwitchWalletTransaction,
	triggerSwitchWalletRemittance,
} from '../../methods/redux/reducers/global-webhooks';
import remakeQueryString from '../../methods/utils/remake-query-string';
import { commas } from '../../methods/utils/general-utils';
import {
	getCurrencies,
	getBlockchainNetworks,
} from '../../methods/redux/reducers/currency';
import FilterRemittanceLogs from './FilterRemittanceLogs';
import SwitchWalletRemittances from './SwitchWalletRemittances';
import SwitchWalletTransactions from './SwitchWalletTransactions';

interface SelectOption {
	name: string;
	value: string;
}

interface TriggerRemittanceProps {
	merchantId: string;
	network: string;
	currency: string;
	walletAddresses: string[];
}

interface RescanTransactionProps {
	merchantId: string;
	network: string;
	currency: string;
	transactionHash: string;
}

const { Text, Title } = Typography;

export const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const SingleRecordLogPage = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const merchantIdParams = searchParams.get('merchantId') || '';
	const [response, setResponse] = useState(null);
	const [openModal, setOpenModal] = useState(false);
	const [openRescanModal, setOpenRescanModal] = useState(false);
	const [triggerState, setTriggerState] = useState<TriggerRemittanceProps>({
		merchantId: merchantIdParams,
		walletAddresses: [''],
		network: '',
		currency: '',
	});
	const [rescanState, setRescanState] = useState<RescanTransactionProps>({
		merchantId: merchantIdParams,
		transactionHash: '',
		network: '',
		currency: '',
	});

	const { currencies, blockchains } = useAppSelector(
		(store) => store.currencies
	);

	const { remittanceReport, loadingReport, triggeringRemittance } =
		useAppSelector((store) => store.globalWebhooks);
	const remittances = remittanceReport?.data?.remittances || [];
	const transactions = remittanceReport?.data?.transactions || [];

	useEffect(() => {
		window.scroll(0, 0);
	}, []);

	// index.tsx (top-level) — DEV-only: suppress noisy ResizeObserver error
	if (typeof window !== 'undefined') {
		window.addEventListener('error', (e: any) => {
			const msg = e?.message || '';
			if (
				msg.includes(
					'ResizeObserver loop completed with undelivered notifications'
				)
			) {
				e.stopImmediatePropagation();
			}
		});
	}

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());

		if (Object.keys(queryData).length > 0) {
			dispatch(getRemittanceReport({ ...queryData }));
		} else {
			setSearchParams({
				merchantId: queryData.merchantId ?? '',
			});
		}

		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, [searchParams]);

	const rescanTransaction = async (record: any) => {
		await dispatch(
			rescanSwitchWalletTransaction({
				merchantId: merchantIdParams,
				transactionHash: record.transactionHash,
				network: record.network,
				currency: record.currency,
			})
		);
	};

	const currencySelect: SelectOption[] = currencies.map((item: any) => ({
		name: item.symbol,
		value: String(item.switchWalletId), // Ensure string
	}));

	const blockchainSelect: SelectOption[] = blockchains.map((item: any) => ({
		name: item.abbreviation,
		value: String(item.switchWalletEnum), // Ensure string
	}));

	const handleAdd = () => {
		setTriggerState((prev) => ({
			...prev,
			walletAddresses: [...prev.walletAddresses, ''],
		}));
	};

	const handleRemove = (index: number) => {
		setTriggerState((prev) => {
			if (prev.walletAddresses.length === 1) return prev; // keep at least 1
			const updated = prev.walletAddresses.filter((_, i) => i !== index);
			return {
				...prev,
				walletAddresses: updated,
			};
		});
	};

	const handleChange = (index: number, value: string) => {
		setTriggerState((prev) => {
			const updated = [...prev.walletAddresses];
			updated[index] = value;
			return {
				...prev,
				walletAddresses: updated,
			};
		});
	};

	const triggerRemittance = async () => {
		const cleanedData = Object.fromEntries(
			Object.entries(triggerState).filter(([_, value]) => value)
		);

		try {
			const res: any = await dispatch(
				triggerSwitchWalletRemittance(cleanedData)
			);

			setResponse(res?.payload?.data);
		} catch (error: any) {
			message.error(error.response.data.message);
			setOpenModal(false);
		}
	};

	const resetTriggerModal = () => {
		setOpenModal(false);
		setTriggerState({
			merchantId: merchantIdParams,
			walletAddresses: [''],
			network: '',
			currency: '',
		});
		setResponse(null);
	};

	const resetRescanModal = () => {
		setOpenRescanModal(false);
		setRescanState({
			merchantId: merchantIdParams,
			transactionHash: '',
			network: '',
			currency: '',
		});
		setResponse(null);
	};

	const rescanTransactionAction = async () => {
		const cleanedData = Object.fromEntries(
			Object.entries(rescanState).filter(([_, value]) => value)
		);

		try {
			const res: any = await dispatch(
				rescanSwitchWalletTransaction(cleanedData)
			);

			setResponse(res?.payload?.data);
		} catch (error: any) {
			message.error(error.response.data.message);
			setOpenModal(false);
		}
	};

	return (
		<div style={{ paddingTop: 24 }}>
			<header style={{ marginBottom: 32 }}>
				<Title level={3}>
					Switch Wallet Remittance Report for Xend Finance
				</Title>
				<Text type="secondary">Real-time monitoring of fund remittances.</Text>

				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'flex-start',
						justifySelf: 'flex-start',
						gap: '10px',
						marginTop: '10px',
					}}
				>
					<Button
						size="middle"
						type="primary"
						block
						onClick={() => setOpenModal(true)}
					>
						Trigger Remittance
					</Button>
					<Button
						size="middle"
						type="primary"
						block
						onClick={() => setOpenRescanModal(true)}
					>
						Trigger Rescan
					</Button>
					<Button
						size="middle"
						type="primary"
						block
						onClick={() =>
							navigate(
								`/compliance/switch-wallet/wallet-balances?merchantId=${merchantIdParams}`
							)
						}
					>
						Switch Wallet Balances
					</Button>
				</div>
			</header>
			{/* Filters */}
			<FilterRemittanceLogs retrieveQueryString={updateQueryString} />
			{/* Reconciliation Summary Cards */}
			<Card style={{ marginBottom: 24 }} title="Reconciliation Summary">
				{loadingReport ? (
					<Spin
						size="large"
						style={{ display: 'block', margin: '40px auto' }}
					/>
				) : (
					<>
						{remittanceReport?.data && (
							<div
								style={{
									display: 'flex',
									justifyContent: 'end',
									marginBottom: '10px',
								}}
							>
								{Number(remittanceReport?.data?.totalRemittanceValue) !==
								Number(remittanceReport?.data?.totalFundingValue) ? (
									<Alert
										message="Warning: Mismatch Detected"
										type="error"
										showIcon
									/>
								) : (
									<Alert message="Systems Balanced" type="success" showIcon />
								)}
							</div>
						)}

						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col xs={24} sm={12} lg={8}>
								<Card>
									<Statistic
										title="Total Funding Txns"
										className="xend-statistic"
										value={`${
											remittanceReport?.data?.totalFundingTransactions
										} (${commas(remittanceReport?.data?.totalFundingValue)})`}
									/>
								</Card>
							</Col>
							<Col xs={24} sm={12} lg={8}>
								<Card>
									<Statistic
										title="Successful Remittances"
										className="xend-statistic"
										value={`${
											remittanceReport?.data?.totalSuccessfulRemittances || 0
										} (${commas(
											remittanceReport?.data?.totalRemittanceValue
										)}) `}
									/>
								</Card>
							</Col>
							<Col xs={24} sm={12} lg={8}>
								<Card>
									<Statistic
										title="Remittable Txns"
										className="xend-statistic"
										value={`${
											remittanceReport?.data?.remittableTransactions || 0
										}`}
									/>
								</Card>
							</Col>
						</Row>

						<Descriptions bordered column={2}>
							<Descriptions.Item label="Report Generated At">
								{remittanceReport?.data?.reportGeneratedAt ? (
									<Text strong>
										{moment(remittanceReport?.data?.reportGeneratedAt).format(
											'MMM DD, YY / hh:mm:ss a'
										)}
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Report Id">
								{remittanceReport?.data?.reportId ? (
									<Text strong>{remittanceReport?.data?.reportId}</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Report Period Start">
								{remittanceReport?.data?.reportPeriodStart ? (
									<Text strong>
										{moment
											.utc(remittanceReport?.data?.reportPeriodStart)
											.format('YYYY-MM-DD HH:mm:ss ([UTC])')}
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Report Period End">
								{remittanceReport?.data?.reportPeriodEnd ? (
									<Text strong>
										{moment
											.utc(remittanceReport?.data?.reportPeriodEnd)
											.format('YYYY-MM-DD HH:mm:ss ([UTC])')}
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>
						</Descriptions>

						<Divider />
						<p>
							How this works: The report is balanced when &lsquo;Total Funding
							Value&lsquo; matches &lsquo;Total Remittance Value&lsquo;. A
							mismatch indicates that some funds have been deposited but not yet
							processed for remittance.
						</p>
					</>
				)}
			</Card>

			{/* Report Table */}
			<Tabs>
				<Tabs.TabPane tab="Remittances" key="1">
					<SwitchWalletRemittances
						remittances={remittances}
						rescanTransaction={rescanTransaction}
					/>
				</Tabs.TabPane>
				<Tabs.TabPane tab="Transactions" key="2">
					<SwitchWalletTransactions
						transactions={transactions}
						rescanTransaction={rescanTransaction}
					/>
				</Tabs.TabPane>
			</Tabs>

			<Modal
				title="Trigger Remittances"
				visible={openModal}
				onOk={triggerRemittance}
				okText={triggeringRemittance ? 'Remitting...' : 'Trigger Remittance'}
				onCancel={resetTriggerModal}
			>
				<h3 style={{ marginBottom: '12px' }}>
					Trigger remittance for specific wallet addresses with optional filters
					Do not trigger for anything less than or equal to 2 USD
				</h3>

				<div style={{ marginBottom: '8px' }}>
					<p>Merchant Id : </p>
					<Input
						placeholder="Merchant Id"
						value={triggerState?.merchantId}
						disabled
					/>
				</div>

				<div style={{ marginBottom: '8px' }}>
					<p>Wallet Addresses</p>
					{triggerState.walletAddresses.map((address: string, index) => (
						<div
							key={address}
							style={{
								display: 'flex',
								alignItems: 'center',
								marginBottom: 8,
							}}
						>
							<Input
								placeholder="Enter Wallet Address"
								value={address}
								onChange={(e) => handleChange(index, e.target.value)}
							/>
							{triggerState.walletAddresses.length > 1 && (
								<Button
									type="text"
									danger
									icon={<MinusCircleOutlined />}
									onClick={() => handleRemove(index)}
									style={{ marginLeft: 8 }}
								/>
							)}
						</div>
					))}

					<Button
						type="dashed"
						icon={<PlusOutlined />}
						onClick={handleAdd}
						style={{ width: '100%' }}
					>
						Add Wallet Address
					</Button>
				</div>

				<div style={{ marginBottom: '8px', width: '100%' }}>
					<p>Network:</p>
					<Select
						allowClear
						placeholder="Select Network"
						value={triggerState.network || undefined}
						style={{ width: '100%' }}
						onChange={(val) =>
							setTriggerState({ ...triggerState, network: val || '' })
						}
					>
						{blockchainSelect.map((optionItem) => (
							<Select.Option key={optionItem.value} value={optionItem.value}>
								{optionItem.name}
							</Select.Option>
						))}
					</Select>
				</div>

				<div style={{ marginBottom: '8px', width: '100%' }}>
					<p>Currency</p>
					<Select
						allowClear
						placeholder="Select Currency"
						value={triggerState.currency || undefined}
						style={{ width: '100%' }}
						onChange={(val) =>
							setTriggerState({ ...triggerState, currency: val || '' })
						}
					>
						{currencySelect
							.filter((item) => item.value !== '')
							.map((optionItem) => (
								<Select.Option
									key={`${optionItem.value}-${optionItem.name}`}
									value={optionItem.value}
								>
									{optionItem.name}
								</Select.Option>
							))}
					</Select>
				</div>

				<div>
					{response && (
						<pre
							style={{
								background: '#f5f5f5',
								padding: '1rem',
								borderRadius: '8px',
								overflowX: 'auto',
							}}
						>
							{JSON.stringify(response, null, 2)}
						</pre>
					)}
				</div>
			</Modal>

			<Modal
				title="Rescan Transaction"
				visible={openRescanModal}
				onOk={rescanTransactionAction}
				okText={triggeringRemittance ? 'Rescanning...' : ' Rescan'}
				onCancel={resetRescanModal}
			>
				<h3 style={{ marginBottom: '12px' }}>
					Rescan a specific transaction by hash.
				</h3>

				<div style={{ marginBottom: '8px' }}>
					<p>Merchant Id: </p>
					<Input
						placeholder="Merchant Id"
						value={rescanState?.merchantId}
						disabled
					/>
				</div>

				<div style={{ marginBottom: '8px' }}>
					<p>Transaction Hash: </p>
					<Input
						placeholder="Transaction Hash"
						value={rescanState?.transactionHash}
						onChange={(e: any) =>
							setRescanState({
								...rescanState,
								transactionHash: e.target.value || '',
							})
						}
					/>
				</div>

				<div style={{ marginBottom: '8px', width: '100%' }}>
					<p>Network:</p>
					<Select
						allowClear
						placeholder="Select Network"
						value={rescanState.network || undefined}
						style={{ width: '100%' }}
						onChange={(val) =>
							setRescanState({ ...rescanState, network: val || '' })
						}
					>
						{blockchainSelect.map((optionItem) => (
							<Select.Option key={optionItem.value} value={optionItem.value}>
								{optionItem.name}
							</Select.Option>
						))}
					</Select>
				</div>

				<div style={{ marginBottom: '8px', width: '100%' }}>
					<p>Currency</p>
					<Select
						allowClear
						placeholder="Select Currency"
						value={rescanState.currency || undefined}
						style={{ width: '100%' }}
						onChange={(val) =>
							setRescanState({ ...rescanState, currency: val || '' })
						}
					>
						{currencySelect
							.filter((item) => item.value !== '')
							.map((optionItem) => (
								<Select.Option
									key={`${optionItem.value}-${optionItem.name}`}
									value={optionItem.value}
								>
									{optionItem.name}
								</Select.Option>
							))}
					</Select>
				</div>

				<div>
					{response && (
						<pre
							style={{
								background: '#f5f5f5',
								padding: '1rem',
								borderRadius: '8px',
								overflowX: 'auto',
							}}
						>
							{JSON.stringify(response, null, 2)}
						</pre>
					)}
				</div>
			</Modal>
		</div>
	);
};

export default SingleRecordLogPage;
